/**
 * HitterSummaryCard — compact single-screen batter overview
 */
import React, { useMemo } from 'react';
import {
  Box, Card, CardContent, Typography, Chip, useTheme, alpha, Divider,
} from '@mui/material';
import PitchGradeCard, { calculateGrade, STAT_THRESHOLDS } from '../charts/PitchGradeCard';

const HitterSummaryCard = ({ player, seasonStats, gameLog = [], year }) => {
  const theme = useTheme();
  const batting = seasonStats?.batting;
  const team = player?.team;
  const name = player?.name;

  // Calculate batting stats
  const stats = useMemo(() => {
    if (!batting) return null;
    const avg = batting.atBats > 0 ? batting.hits / batting.atBats : 0;
    const pa = batting.atBats + (batting.baseOnBalls || 0) + (batting.hitByPitch || 0) + (batting.sacrificeFlies || 0);
    const obp = pa > 0 ? (batting.hits + batting.baseOnBalls + (batting.hitByPitch || 0)) / pa : 0;
    const tb = batting.hits + (batting.doubles || 0) + (2 * (batting.triples || 0)) + (3 * (batting.homeRuns || 0));
    const slg = batting.atBats > 0 ? tb / batting.atBats : 0;
    const ops = obp + slg;
    const iso = slg - avg;
    const kRate = pa > 0 ? (batting.strikeOuts || 0) / pa : 0;
    const bbRate = pa > 0 ? (batting.baseOnBalls || 0) / pa : 0;
    const hrRate = pa > 0 ? (batting.homeRuns || 0) / pa : 0;
    const babip = batting.babip || null;
    const woba = batting.wOBA || batting.woba || null;

    return { avg, obp, slg, ops, iso, kRate, bbRate, hrRate, babip, woba, pa, tb };
  }, [batting]);

  if (!batting || !stats) return null;

  const keyStats = [
    { label: 'H',   value: batting.hits         || 0 },
    { label: 'HR',  value: batting.homeRuns      || 0 },
    { label: 'RBI', value: batting.rbi           || 0 },
    { label: 'R',   value: batting.runs          || 0 },
    { label: 'SB',  value: batting.stolenBases   || 0 },
    { label: 'BB',  value: batting.baseOnBalls   || 0 },
    { label: 'K',   value: batting.strikeOuts    || 0 },
    { label: 'K%',  value: (stats.kRate  * 100).toFixed(0) + '%' },
    { label: 'BB%', value: (stats.bbRate * 100).toFixed(0) + '%' },
    ...(stats.babip ? [{ label: 'BABIP', value: stats.babip.toFixed(3) }] : []),
    ...(stats.woba  ? [{ label: 'wOBA',  value: stats.woba.toFixed(3)  }] : []),
  ];

  return (
    <Card elevation={0} sx={{ border: `1px solid ${alpha(theme.palette.primary.main, 0.22)}`, overflow: 'hidden', borderRadius: 2 }}>
      {/* Gradient header */}
      <Box sx={{ px: 2, py: 1.25, background: `linear-gradient(135deg, ${theme.palette.primary.dark}, ${theme.palette.primary.main})`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="subtitle2" fontWeight={700} sx={{ fontSize: '0.82rem', color: 'white', letterSpacing: '0.05em', textTransform: 'uppercase' }}>
          Batting Summary
        </Typography>
        <Box sx={{ display: 'flex', gap: 0.75 }}>
          {[`${batting.gamesPlayed || 0}G`, `${batting.atBats || 0}AB`, `${stats.pa}PA`].map((lbl, i) => (
            <Chip key={i} label={lbl} size="small" sx={{ fontWeight: 700, fontSize: '0.72rem', bgcolor: alpha('#fff', 0.18), color: 'white', border: 'none', height: 22 }} />
          ))}
        </Box>
      </Box>

      <CardContent sx={{ p: 2 }}>
        {/* Grade cards */}
        <Box sx={{ display: 'flex', gap: 1.25, flexWrap: 'wrap', mb: 2 }}>
          <PitchGradeCard label="AVG"  grade={calculateGrade(stats.avg,  STAT_THRESHOLDS.batting.avg)}  value={stats.avg.toFixed(3)} />
          <PitchGradeCard label="OBP"  grade={calculateGrade(stats.obp,  STAT_THRESHOLDS.batting.obp)}  value={stats.obp.toFixed(3)} />
          <PitchGradeCard label="SLG"  grade={calculateGrade(stats.slg,  STAT_THRESHOLDS.batting.slg)}  value={stats.slg.toFixed(3)} />
          <PitchGradeCard label="OPS"  grade={calculateGrade(stats.ops,  STAT_THRESHOLDS.batting.ops)}  value={stats.ops.toFixed(3)} />
          <PitchGradeCard label="ISO"  grade={calculateGrade(stats.iso,  STAT_THRESHOLDS.batting.iso)}  value={stats.iso.toFixed(3)} />
          {stats.woba && <PitchGradeCard label="wOBA" grade={calculateGrade(stats.woba, STAT_THRESHOLDS.batting.woba)} value={stats.woba.toFixed(3)} />}
        </Box>

        <Divider sx={{ mb: 1.5, opacity: 0.4 }} />

        {/* Counting / rate stats bar */}
        <Box sx={{ display: 'flex', flexWrap: 'wrap' }}>
          {keyStats.map((s, i) => (
            <Box key={i} sx={{
              textAlign: 'center', px: 1.5, py: 0.75,
              borderRight: i < keyStats.length - 1 ? `1px solid ${alpha(theme.palette.divider, 0.4)}` : 'none',
            }}>
              <Typography fontWeight={800} sx={{ fontSize: '1.05rem', lineHeight: 1.1 }}>{s.value}</Typography>
              <Typography variant="caption" sx={{ fontSize: '0.68rem', color: 'text.secondary', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{s.label}</Typography>
            </Box>
          ))}
        </Box>
      </CardContent>
    </Card>
  );
};

export default HitterSummaryCard;
